import { ReparoApi, BASE_API_URL } from './index';
import { getAuthToken } from './auth';

export type EntityType =
    | 'PRODUCT'
    | 'VENDOR'
    | 'VENDOR_PRODUCT_MAPPING'
    | 'CHANNEL'
    | 'WAREHOUSE'
    | 'MATERIAL';
export type MasterEntityType =
    | 'PRODUCT'
    | 'VENDOR'
    | 'VENDOR_PRODUCT_MAPPING'
    | 'VENDOR_MATERIAL_MAPPING'
    | 'VENDOR_PRICE'
    | 'WAREHOUSE'
    | 'CHANNEL'
    | 'MATERIAL';

export type EntityAttributeDefinition = {
    id: string;
    entityType: EntityType;
    name: string;
    key: string;
    valueCount?: number;
    columnType?: 'STATIC' | 'DYNAMIC';
    showInFilter?: boolean;
    cronExpression?: string;
    createdAt: string;
    updatedAt: string;
};

export type UploadValuesResponse = {
    uploaded: number;
    skipped: number;
    unrecognizedIds: string[];
};

export type ValidateValuesResponse = {
    parsedCount: number;
    distinctValues: string[];
    missingEntityIds: string[];
    unrecognizedEntityIds: string[];
};

export type AssignedValue = {
    entityId: string;
    value: string;
};

export type DistinctValuesResponse = {
    values: string[];
};

export type MatchingEntityIdsResponse = {
    entityIds: string[];
};

/** Metadata for a single column in the master table response */
export type MasterTableColumnMeta = {
    key: string;
    header: string;
    /** CATEGORY_CAPACITY = one retail category's cap on a warehouse; saved through the core PATCH. */
    type: 'CORE' | 'ATTRIBUTE' | 'CATEGORY_CAPACITY';
    editable: boolean;
    attributeId?: string;
    showInFilter?: boolean;
    columnType?: 'STATIC' | 'DYNAMIC';
};

export type EntityMasterTableResponse = {
    rows: Record<string, unknown>[];
    total: number;
    page: number;
    pageSize: number;
    columns: MasterTableColumnMeta[];
};

export type FilterParamOption = { label: string; value: string };
export type FilterParamResponse = {
    key: string;
    label: string;
    mode: 'multi' | 'single';
    options: FilterParamOption[];
};

// ---------------------------------------------------------------------------
// Master table
// ---------------------------------------------------------------------------

export const getEntityMasterFilterOptions = async (
    entityType: MasterEntityType,
): Promise<Record<string, string[]>> => {
    return ReparoApi<Record<string, string[]>>({
        method: 'GET',
        url: `/entity-attributes/master/filter-options?entityType=${entityType}`,
    });
};

export const getEntityMasterTable = async (dto: {
    entityType: MasterEntityType;
    page: number;
    pageSize: number;
    search?: string;
    filterEntityIds?: string[];
    coreFilters?: Record<string, string[]>;
}): Promise<EntityMasterTableResponse> => {
    return ReparoApi<EntityMasterTableResponse>({
        method: 'POST',
        url: '/entity-attributes/master',
        body: JSON.stringify(dto),
    });
};

// ---------------------------------------------------------------------------
// Attribute definition CRUD
// ---------------------------------------------------------------------------

export const getEntityAttributes = async (
    entityType: EntityType,
): Promise<EntityAttributeDefinition[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/entity-attributes?entityType=${entityType}`,
    });
};

export const createEntityAttribute = async (dto: {
    entityType: EntityType;
    name: string;
    showInFilter?: boolean;
}): Promise<EntityAttributeDefinition> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/entity-attributes',
        body: JSON.stringify(dto),
    });
};

export const updateEntityAttribute = async (
    id: string,
    dto: { name?: string; showInFilter?: boolean },
): Promise<EntityAttributeDefinition> => {
    return ReparoApi<any>({
        method: 'PATCH',
        url: `/entity-attributes/${id}`,
        body: JSON.stringify(dto),
    });
};

export const deleteEntityAttribute = async (id: string): Promise<void> => {
    return ReparoApi<any>({
        method: 'DELETE',
        url: `/entity-attributes/${id}`,
    });
};

// ---------------------------------------------------------------------------
// Attribute value operations
// ---------------------------------------------------------------------------

/** Upsert a single attribute value for one entity (inline cell edit). */
export const upsertAttributeValue = async (
    definitionId: string,
    entityId: string,
    value: string,
): Promise<void> => {
    await ReparoApi<{ updated: boolean }>({
        method: 'PATCH',
        url: `/entity-attributes/${definitionId}/values/${encodeURIComponent(entityId)}`,
        body: JSON.stringify({ value }),
    });
};

/**
 * Clear one entity's value for one attribute. The row is removed rather than blanked: the
 * value column is a string, and '' would still read as a value to every filter and export.
 */
export const clearAttributeValue = async (
    definitionId: string,
    entityId: string,
): Promise<void> => {
    await ReparoApi<void>({
        method: 'DELETE',
        url: `/entity-attributes/${definitionId}/values/${encodeURIComponent(entityId)}`,
    });
};

/** Patch a core entity column (name, category, etc.) for one entity (inline cell edit). */
export const patchCoreField = async (
    entityType: MasterEntityType,
    entityId: string,
    key: string,
    value: string,
): Promise<void> => {
    await ReparoApi<{ updated: boolean }>({
        method: 'PATCH',
        url: `/entity-attributes/master/${entityType}/rows/${encodeURIComponent(entityId)}`,
        body: JSON.stringify({ key, value }),
    });
};

export const validateEntityAttributeValues = async (
    entityType: EntityType,
    file: File,
): Promise<ValidateValuesResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/entity-attributes/values/validate?entityType=${entityType}`,
        body: formData,
    });
};

export const uploadEntityAttributeValues = async (
    id: string,
    file: File,
    assignedValues?: AssignedValue[],
): Promise<UploadValuesResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (assignedValues && assignedValues.length > 0) {
        formData.append('assignedValues', JSON.stringify(assignedValues));
    }
    return ReparoApi<any>({
        method: 'POST',
        url: `/entity-attributes/${id}/values/upload`,
        body: formData,
    });
};

export const downloadEntityAttributeTemplate = (entityType: EntityType): void => {
    const apiPrefix = BASE_API_URL ? `${BASE_API_URL}/api/v1` : '/api/v1';
    const token = getAuthToken();
    const url = `${apiPrefix}/entity-attributes/template?entityType=${entityType}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.blob())
        .then((blob) => {
            const anchor = document.createElement('a');
            anchor.href = URL.createObjectURL(blob);
            anchor.setAttribute('download', `${entityType.toLowerCase()}-template.csv`);
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(anchor.href);
        })
        .catch(console.error);
};

export const downloadEntityAttributeValues = (id: string): void => {
    const apiPrefix = BASE_API_URL ? `${BASE_API_URL}/api/v1` : '/api/v1';
    const token = getAuthToken();
    const url = `${apiPrefix}/entity-attributes/${id}/values/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.blob())
        .then((blob) => {
            const anchor = document.createElement('a');
            anchor.href = URL.createObjectURL(blob);
            anchor.setAttribute('download', 'attribute-values.csv');
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(anchor.href);
        })
        .catch(console.error);
};

export const getEntityAttributeDistinctValues = async (
    id: string,
): Promise<DistinctValuesResponse> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/entity-attributes/${id}/distinct-values`,
    });
};

export const getMatchingEntityIds = async (
    id: string,
    values: string[],
): Promise<MatchingEntityIdsResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: `/entity-attributes/${id}/matching-entity-ids`,
        body: JSON.stringify({ values }),
    });
};

// ---------------------------------------------------------------------------
// Filter params (for showInFilter attributes)
// ---------------------------------------------------------------------------

export type BulkUpdateResult = {
    updated: number;
    skipped: number;
    errors: string[];
};

/** Bulk update entity master records from a CSV file (toolbar upload). */
export const bulkUpdateMasterCsv = async (
    entityType: MasterEntityType,
    file: File,
): Promise<BulkUpdateResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<BulkUpdateResult>({
        method: 'POST',
        url: `/entity-attributes/master/${entityType}/bulk-csv`,
        body: formData,
    });
};

export const getEntityAttributeFilterParams = async (
    entityType: EntityType,
): Promise<FilterParamResponse[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/entity-attributes/filter-params?entityType=${entityType}`,
    });
};
